/**
 * Google Analytics 4.
 *
 * Bật bằng biến môi trường REACT_APP_GA4_ID (dạng G-XXXXXXXXXX).
 * Không có ID thì toàn bộ file này là no-op: không nạp script, không gửi
 * request nào — an toàn để merge trước khi có tài khoản GA4.
 *
 * Vì sao phải tự gửi page_view:
 * Đây là SPA, gtag chỉ tự bắn page_view đúng một lần lúc tải trang. Mọi lần
 * chuyển route sau đó phải tự gửi (xem trackPageView + router.subscribe
 * trong src/index.tsx).
 *
 * Vì sao gửi *mẫu route* chứ không phải đường dẫn thật:
 * App có 14 route mang tham số (/league/:id, /league/:leagueId/match/:matchId…).
 * Nếu gửi đường dẫn thật, báo cáo GA4 sẽ vỡ vụn thành hàng nghìn dòng mỗi
 * dòng 1 lượt xem. Nên page_path gửi mẫu "/league/:id", còn id cụ thể đi
 * kèm dưới dạng tham số của event nghiệp vụ (vd league_id trong league_register).
 */

const GA4_ID = process.env.REACT_APP_GA4_ID ?? "";
const ENVIRONMENT = process.env.REACT_APP_ENV ?? "development";
/**
 * Bật debug_mode để event hiện trong GA4 Admin → DebugView mà người test
 * không phải cài extension. Chỉ nên bật ở UAT (property riêng), tuyệt đối
 * không bật ở production.
 */
const GA4_DEBUG = process.env.REACT_APP_GA4_DEBUG === "true";

/** GA4 có đang bật không (dùng để bọc các lời gọi tuỳ chọn). */
export const isAnalyticsEnabled = () => Boolean(GA4_ID);

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

let initialised = false;
/** Giữ lại để đính vào từng event — xem setAnalyticsUser. */
let currentUserId: string | undefined;

/** Nạp gtag.js và cấu hình. Gọi một lần lúc khởi động app. */
export const initAnalytics = () => {
  if (!GA4_ID || initialised || typeof window === "undefined") return;
  initialised = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    GA4_ID
  )}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag yêu cầu đẩy nguyên `arguments`, không được spread thành mảng.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, {
    // Tự gửi page_view ở trackPageView để kiểm soát được page_path.
    send_page_view: false,
    // Đặt ở `config` để áp cho mọi event, kể cả event tự động của GA4.
    ...(GA4_DEBUG ? { debug_mode: true } : {}),
    // Phân biệt dữ liệu UAT với production khi dùng chung một property.
    // NODE_ENV luôn là "production" với mọi bản build CRA nên vô dụng ở đây.
    environment: ENVIRONMENT,
  });
};

/** Gửi event tuỳ ý. Tên và tham số theo quy ước snake_case của GA4. */
export const trackEvent = (
  name: string,
  params: Record<string, any> = {}
) => {
  if (!GA4_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, {
    ...params,
    ...(currentUserId ? { user_id: currentUserId } : {}),
  });
};

/**
 * Gắn id người dùng để GA4 nối phiên trên nhiều thiết bị.
 * Chỉ truyền id nội bộ (UUID). TUYỆT ĐỐI không truyền số điện thoại, email
 * hay tên — chính sách GA4 cấm gửi thông tin định danh cá nhân, vi phạm có
 * thể bị xoá dữ liệu hoặc khoá property.
 */
export const setAnalyticsUser = (userId?: string | null) => {
  if (!GA4_ID) return;
  currentUserId = userId || undefined;
  if (typeof window === "undefined" || !window.gtag) return;
  // Đã thử cả gtag("set", {user_id}) lẫn gọi lại gtag("config", ...): cả hai
  // đều KHÔNG đẩy user_id lên wire (kiểm chứng bằng cách bắt request — không
  // hit nào chứa UUID). Cách chắc chắn là đính vào tham số của từng event,
  // xử lý trong trackEvent/trackPageView.
  window.gtag("set", { user_id: currentUserId });
};

/** Xoá liên kết người dùng khi đăng xuất. */
export const clearAnalyticsUser = () => setAnalyticsUser(null);

/**
 * Dựng mẫu route từ danh sách match của react-router.
 * Ví dụ: /league/9a82…/detail  ->  /league/:id/detail
 */
export const routePatternFrom = (matches: any[] | undefined): string => {
  if (!matches || !matches.length) return window.location.pathname;
  const segments = matches
    .map((m) => m?.route?.path ?? "")
    .filter((p) => p && p !== "/");
  if (!segments.length) return "/";
  return (
    "/" +
    segments
      .join("/")
      .replace(/\/+/g, "/")
      .replace(/^\/|\/$/g, "")
  );
};

/** Gửi một lượt xem trang. */
export const trackPageView = (pattern: string, title?: string) => {
  if (!GA4_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: pattern,
    page_location: window.location.origin + pattern,
    page_title: title ?? document.title,
    ...(currentUserId ? { user_id: currentUserId } : {}),
  });
};
